import { handleActions } from 'redux-actions';
import { createSelector } from 'reselect';
import AuthTypes from '../types/AuthType';
import { getCapabilitiesByRoles, setUserAbilities } from '../../utils/Capability';
import LocalStorageHelper from '../../utils/LocalStorageHelper';
import { AuthKeys } from '../../utils/Constants';

const initialAuthorization = {
  roles: [],
  capabilities: [],
};
const initialUserData = {
  mfaEnabled: false,
  idUser: LocalStorageHelper.getData(AuthKeys.ID_USER) ? LocalStorageHelper.getData(AuthKeys.ID_USER) : '',
  qrUrl: '',
  language: LocalStorageHelper.getData(AuthKeys.LANGUAGE) ? LocalStorageHelper.getData(AuthKeys.LANGUAGE) : '',
  userMerchants: [],
  transferEnabled: false,
  uploadVoucherEnabled: false,
};
const initialState = {
  isAuthenticated: !!LocalStorageHelper.getData(AuthKeys.IS_AUTHENTICATED),
  isMfaValid: false,
  activeSession: true,
  loggedIn: false,
  logout: false,
  userData: LocalStorageHelper.getData(AuthKeys.USER_DATA) ? LocalStorageHelper.getData(AuthKeys.USER_DATA) : initialUserData,
  authorization: LocalStorageHelper.getData(AuthKeys.AUTHORIZATION) ? LocalStorageHelper.getData(AuthKeys.AUTHORIZATION) : initialAuthorization,
};

const genAuthorization = ({ roles, capabilities }) => {
  let capabilitiesList = [];
  if (roles.length) {
    capabilitiesList = getCapabilitiesByRoles(roles);
  } else capabilitiesList = capabilities;
  setUserAbilities(capabilitiesList);
  return { roles, capabilities: capabilitiesList, astropay: roles && roles.length > 0 };
};

export const User = handleActions(
  {
    [AuthTypes.actions.login]: (state) => {
      return { ...state, isMfaValid: false };
    },
    [AuthTypes.actions.loginSuccess]: (state, { payload: { userData, isAuthenticated, authorization } }) => {
      return { ...state, userData, isAuthenticated, loggedIn: true, authorization: genAuthorization(authorization) };
    },
    [AuthTypes.actions.switchContextSuccess]: (state, { payload: { userData, authorization } }) => {
      return { ...state, userData, authorization: genAuthorization(authorization) };
    },
    [AuthTypes.actions.authMfaSuccess]: (state, { payload: { userData, idMerchant, authorization } }) => {
      return { ...state, isMfaValid: true, isAuthenticated: true, userData, idMerchant, authorization: genAuthorization(authorization) };
    },
    [AuthTypes.actions.signInSuccess]: (state, { payload: userData }) => {
      return { ...state, userData };
    },
    [AuthTypes.actions.activateMfaSuccess]: (state) => {
      return { ...state, isMfaValid: true };
    },
    [AuthTypes.actions.resetMFASuccess]: (state) => {
      return { ...state, isMfaValid: true };
    },
    [AuthTypes.actions.forgotPassSuccess]: (state) => {
      return { ...state };
    },
    [AuthTypes.actions.resetPassSuccess]: (state) => {
      return { ...state };
    },
    [AuthTypes.actions.logoutSuccess]: () => {
      setUserAbilities([]);
      return { ...initialState, isAuthenticated: false, activeSession: false, logout: true, authorization: initialAuthorization, userData: initialUserData };
    },
    [AuthTypes.actions.updateLocalUserData]: (state, { payload: userData }) => {
      return { ...state, userData: { ...state.userData, ...userData } };
    },
  },
  initialState
);

export const getUser = (state) => state.User || initialState;

export const isAuthenticated = createSelector(getUser, (user) => user.isAuthenticated);

export const isLoggedIn = createSelector(getUser, (user) => user.loggedIn);

export const recentLogout = createSelector(getUser, (user) => user.logout);

export const isSessionActive = createSelector(getUser, (user) => user.activeSession);

export const getUserData = createSelector(getUser, (user) => user.userData);

export const getIdMerchant = createSelector(getUserData, (user) => user.idMerchant);

export const isMfaEnabled = createSelector(getUserData, (userData) => userData.mfaEnabled);

export const isMFAValid = createSelector(getUser, (user) => user.isMfaValid);

export const idUser = createSelector(getUserData, (userData) => userData.idUser);

export const language = createSelector(getUserData, (userData) => userData.language);

export const mfaBarcode = createSelector(getUserData, (userData) => userData.qrUrl);

const getAuthorization = createSelector(getUser, (user) => user.authorization);

export const isAdmin = createSelector(getAuthorization, (authorization) => authorization.admin);

export const isInternalUser = createSelector(getAuthorization, (authorization) => authorization.astropay);

export const referralEnabled = createSelector(getUserData, (userData) => userData.referral);

export const getAuthCapabilities = createSelector(getAuthorization, (authorization) => authorization.capabilities);

export const getUserDataCapabilities = createSelector(getUserData, (userData) => userData.capabilities);

export const isMultiMerchant = createSelector(getUserData, (user) => user.userMerchants.length > 1);

export const getMultiMerchantList = createSelector(getUserData, (user) => user.userMerchants);

export const isMerchantAdmin = createSelector(getUserData, (userData) => !userData.astropay && userData.admin);

export const isTransferEnabled = createSelector(getUserData, (userData) => userData.transferEnabled);

export const isUploadVoucherEnabled = createSelector(getUserData, (userData) => userData.uploadVoucherEnabled);
